import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Breadcrumb from "app/components/Breadcrumb";
import TwoFactorSetup from "./TwoFactorSetup";
import ChangePassword from "./ChangePassword";

// Simple chevron, drawn inline so we don't need a new icon dependency.
function ChevronRightIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6c757d"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function GreenCheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      style={{ marginRight: 8 }}
    >
      <circle cx="12" cy="12" r="10" fill="#10B981" />
      <path
        d="M8.5 12.5L10.5 14.5L15.5 9.5"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RedCrossIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      style={{ marginRight: 8 }}
    >
      <circle cx="12" cy="12" r="10" fill="#EF4444" />
      <path
        d="M8.5 8.5L15.5 15.5M15.5 8.5L8.5 15.5"
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SecurityNavItem({ icon, label, onClick, isEnabled, statusText }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick?.()}
      className="d-flex align-items-center justify-content-between px-3 py-3 mb-3"
      style={{
        backgroundColor: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        cursor: "pointer",
        maxWidth: 660,
        width: "100%"
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <img src={icon} alt="" style={{ width: 20, height: 20 }} />
        <span>{label}</span>
      </div>
      <div className="d-flex align-items-center">
        {statusText && (
          <span style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            color: isEnabled ? '#10B981' : '#EF4444', 
            marginRight: '8px' 
          }}>
            {statusText}
          </span>
        )}
        {isEnabled !== undefined && (isEnabled ? <GreenCheckIcon /> : <RedCrossIcon />)}
        <ChevronRightIcon />
      </div>
    </div>
  );
}

export default function SecurityConnexion() {
  const { t } = useTranslation("settings");
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const is2faEnabled = Boolean(user?.twoFactorEnabled);

  const routeSegments = [
    { name: t("breadcrumbs.settings"), path: "/LoginSecurity" },
    { name: t("breadcrumbs.security") }
  ];

  return (
    <div>
      <Breadcrumb routeSegments={routeSegments} />

      <Card className="shadow-sm p-4" style={{ minHeight: "70vh" }}>
        <h5 className="mb-1">{t("loginSecurity.title")}</h5>
        <p className="text-muted mb-4">{t("loginSecurity.description")}</p>

        <SecurityNavItem
          icon="/assets/icons/settingsIcons/password.png"
          label={t("loginSecurity.items.changePassword")}
          onClick={() => setShowChangePasswordModal(true)}
        />

        <SecurityNavItem
          icon="/assets/icons/settingsIcons/two-factor-authentication.png"
          label={t("loginSecurity.items.twoFactorAuth")}
          onClick={() => setShow2FAModal(true)}
          isEnabled={is2faEnabled}
          statusText={is2faEnabled ? t("loginSecurity.items.enabled") : t("loginSecurity.items.disabled")}
        />
      </Card>

      <TwoFactorSetup show={show2FAModal} onClose={() => setShow2FAModal(false)} />
      <ChangePassword
        show={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </div>
  );
}